/**
 * Single markdown chokepoint for epi-chat.
 *
 * - GFM on (tables, strikethrough, task lists, autolinks)
 * - Raw HTML off — we never inject model-produced HTML into the DOM
 * - Code blocks routed through react-syntax-highlighter (already in deps)
 * - Typography uses MUI theme so the renderer inherits VDS tokens
 */

import { Box, Link, SxProps, Theme, Typography } from '@mui/material';
import type { Code, Root, Text } from 'mdast';
import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import remarkGfm from 'remark-gfm';
import type { Plugin } from 'unified';
import type { Parent } from 'unist';

import { ConceptGroupDetailsDialog } from '@verantos-dev/feature-concept-groups-list';

import { useActiveDataset } from '../context/ActiveDatasetContext';
import { useAvSlugClick } from '../context/AvSlugClickContext';
import { useConceptGroupNav } from '../context/ConceptGroupNavContext';
import { useKnownAvSlugs } from '../context/KnownAvSlugsContext';

interface MarkdownProps {
  children: string;
  sxProps?: SxProps<Theme>;
  dense?: boolean;
}

// Models occasionally emit `<br>` (or `<br/>` / `<br />`) as a standalone
// text part — usually a spacer between two tool calls, sometimes as a line
// break inside a paragraph. With raw HTML disabled (see file header), the
// renderer would otherwise show the literal tag. Normalize the safe void
// form into a markdown hard break (`  \n`) and let `isEffectivelyEmpty`
// suppress the part entirely if nothing else is left.
const SAFE_BR_PATTERN = /<br\s*\/?>/gi;

// ``input`` is typed ``string`` by callers, but the content ultimately comes
// from untrusted wire data (e.g. a checkpoint ``format`` the agent emitted as a
// structured object instead of prose). A non-string here used to throw
// ``.replace is not a function`` and take down the whole card via the error
// boundary — coerce defensively so a leaf renderer never crashes the page.
const normalizeBrTags = (input: unknown): string =>
  (typeof input === 'string' ? input : '').replace(SAFE_BR_PATTERN, '  \n');

const isEffectivelyEmpty = (input: string): boolean => input.trim().length === 0;

// Concept group IDs follow `<digits>-<org-suffix>` where the suffix is the
// owning org slug — `Verantos` for curated groups, plus org-level slugs like
// `Amgen`, `qa-org`, `verantos`, etc. (see TOOLING_DEV.PLATFORM.ALL_CONCEPT_GROUP).
// Require 4+ leading digits and a letter-led suffix so we don't match tokens like
// `2025-04` or arbitrary kebab strings.
const CONCEPT_GROUP_ID_PATTERN = /^\d{4,}-[A-Za-z][A-Za-z0-9_-]*$/;

// Wider pattern used by the prose-autolinker remark plugin below. Real
// concept-group ids are 16+ digit Verantos snowflake values; tightening to
// 10+ digits here keeps year-codes (`2025-Q4`) and short numeric tokens out
// while still matching every real id we ship. Word boundaries on both sides
// avoid mid-token matches.
const CONCEPT_GROUP_ID_PROSE_PATTERN = /\b\d{10,}-[A-Za-z][A-Za-z0-9_]*\b/g;

// OR-6115: the agent emits an in-app link to a *locked library* analytical
// variable as a real markdown link to its detail route — e.g.
// `[first_asthma_diagnosis_date](/projects/<pid>/variable/<uuid>)`. Unlike a
// backtick slug (which auto-links to the unsaved `av.<slug>` draft), this
// carries the library UUID so the click resolves to the curated ("Saved")
// record. Match the variable id (UUID or `av.<slug>`) out of such an href so
// the `a` renderer can route it internally instead of opening a new tab.
const PROJECT_VARIABLE_HREF_PATTERN = /\/projects\/[^/]+\/variable\/([^/?#]+)/;

const parseProjectVariableHref = (href: string | undefined): string | null => {
  if (!href) return null;
  const match = PROJECT_VARIABLE_HREF_PATTERN.exec(href);
  return match ? decodeURIComponent(match[1]) : null;
};

// OR-6266: a project-LESS variable ref the drafter can emit from prose without
// knowing the project id — `[<label>](/variable/av.<slug>)` or
// `[<label>](/variable/<uuid>)`. The rendering surface's avSlugClick handler
// supplies the project id (and prefixes `av.` for a bare slug), so the href
// only carries the identity. Anchored on `/variable/` so it never hijacks an
// absolute external URL, mirroring the concept-group name-link and OR-6115
// variable-link paths. The OR-6115 `/projects/<pid>/variable/<id>` form above
// is unchanged.
const AV_REF_HREF_PATTERN =
  /^\/variable\/(av\.[a-z0-9_]+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:[?#]|$)/i;

const parseAvRefHref = (href: string | undefined): string | null => {
  if (!href) return null;
  const match = AV_REF_HREF_PATTERN.exec(href);
  return match ? decodeURIComponent(match[1]) : null;
};

// OR-6129: the agent refers to a concept group by its human-readable NAME,
// emitted as a markdown link `[Asthma diagnosis](/concept-group/<id>)`. The
// name is the visible, clickable label; the id rides in the href so the click
// still opens the preview — drill-in is retained without ever showing a bare
// id. Mirrors the OR-6115 variable-link pattern above. The bare-id autolinker
// (`remarkLinkifyConceptGroupIds`) remains as a fallback for raw ids.
const CONCEPT_GROUP_HREF_PATTERN = /\/concept-group\/([^/?#]+)/;

const parseConceptGroupHref = (href: string | undefined): string | null => {
  if (!href) return null;
  const match = CONCEPT_GROUP_HREF_PATTERN.exec(href);
  return match ? decodeURIComponent(match[1]) : null;
};

/**
 * Remark plugin: scan plain text nodes for concept-group ids and rewrite
 * each match as an inline-code node. Once they're inline-code nodes, the
 * existing `code` component override (further down) wires the click-to-
 * open affordance — so prose like "concept group 3032889331786151605-Verantos"
 * becomes a clickable identifier without the agent having to remember to
 * wrap ids in backticks.
 *
 * The walker skips `code` and `inlineCode` nodes so anything the author
 * already wrapped in backticks passes through untouched. Defense in depth
 * with the system-prompt guidance — the prompt asks the agent to wrap ids,
 * but historical artifacts and any agent slip-ups still render correctly.
 */
const remarkLinkifyConceptGroupIds: Plugin<[], Root> = () => (tree) => {
  const visit = (node: Parent) => {
    if (!Array.isArray(node.children)) return;
    const next: typeof node.children = [];
    for (const child of node.children) {
      if (child.type === 'text') {
        const value = (child as Text).value;
        // Reset lastIndex on each node so the global regex doesn't carry
        // state across siblings.
        CONCEPT_GROUP_ID_PROSE_PATTERN.lastIndex = 0;
        let lastIndex = 0;
        let match: RegExpExecArray | null;
        let replaced = false;
        while ((match = CONCEPT_GROUP_ID_PROSE_PATTERN.exec(value)) !== null) {
          replaced = true;
          if (match.index > lastIndex) {
            next.push({ type: 'text', value: value.slice(lastIndex, match.index) } as Text);
          }
          next.push({ type: 'inlineCode', value: match[0] } as never);
          lastIndex = match.index + match[0].length;
        }
        if (replaced) {
          if (lastIndex < value.length) {
            next.push({ type: 'text', value: value.slice(lastIndex) } as Text);
          }
        } else {
          next.push(child);
        }
      } else {
        // Recurse into structural nodes (paragraph, heading, list, table
        // cell, emphasis, link). Skip `code` and `inlineCode` so existing
        // backticked content is never re-wrapped.
        if (child.type !== 'code' && child.type !== 'inlineCode') {
          visit(child as Parent);
        }
        next.push(child);
      }
    }
    node.children = next;
  };
  visit(tree as Parent);
};

// OR-6201: the concept-group proposer narrates a fenced ```json block holding
// the proposed group's envelope ({concept_group_id, source: "proposed_new",
// definition: {...}}). That exact proposal already renders as the concept-group
// card / detail panel beside the chat, so the raw JSON in the working-state
// conversation is pure duplication. Drop those fenced code nodes. The match is
// deliberately strict (envelope shape, not "any json") so legitimate JSON the
// agent shows stays visible.
const isConceptGroupProposalEnvelope = (raw: string): boolean => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return false;
  }
  if (typeof parsed !== 'object' || parsed === null) return false;
  const obj = parsed as Record<string, unknown>;
  return (
    typeof obj.concept_group_id === 'string' &&
    obj.source === 'proposed_new' &&
    typeof obj.definition === 'object' &&
    obj.definition !== null
  );
};

const remarkStripConceptGroupProposalJson: Plugin<[], Root> = () => (tree) => {
  const visit = (node: Parent) => {
    if (!Array.isArray(node.children)) return;
    node.children = node.children.filter(
      (child) => !(child.type === 'code' && isConceptGroupProposalEnvelope((child as Code).value))
    );
    for (const child of node.children) {
      visit(child as Parent);
    }
  };
  visit(tree as Parent);
};

// analytical variable slugs in checkpoint summaries (e.g. `severe_asthma_index_date`,
// `bmi_at_index`) reference knowledge entries keyed `av.<slug>`. The pattern
// is the structural prerequisite — 2+ underscore-separated lowercase words —
// but pattern matching alone is not enough: it false-positives on common
// SQL identifiers like `value_as_number` and `concept_group_id`. When the
// surrounding scope provides a known-slug allowlist (via
// {@link KnownAvSlugsProvider}), we additionally require membership.
const AV_SLUG_PATTERN = /^[a-z][a-z0-9]*(_[a-z0-9]+)+$/;

export const Markdown = ({ children, sxProps, dense = false }: MarkdownProps) => {
  const [openConceptGroupId, setOpenConceptGroupId] = useState<string | null>(null);
  // In the project chat, a concept-group link routes to the in-pane artifact
  // (breadcrumb back); on the standalone surface (no provider) it opens the modal.
  const { navigateToConceptGroup } = useConceptGroupNav();
  const avSlugClick = useAvSlugClick();
  const knownAvSlugs = useKnownAvSlugs();
  // TODO: cleanup with concept_group_prevalence_preview ff
  const activeDataset = useActiveDataset();
  const normalized = normalizeBrTags(children);
  // A text part that collapses to empty (the model emitting `<br>` alone as
  // a spacer between tool calls) renders nothing — better than an empty
  // bubble or a stray br token.
  if (isEffectivelyEmpty(normalized)) {
    return null;
  }
  const isLinkableAvSlug = (value: string): boolean => {
    if (!AV_SLUG_PATTERN.test(value)) return false;
    // No allowlist provided — pattern-only fallback for callers outside the
    // chat scope (knowledge browser, etc.). Tighten the pattern to 3+ words
    // here to keep parity with the prior heuristic.
    if (knownAvSlugs === null) return /^[a-z][a-z0-9]*(_[a-z0-9]+){2,}$/.test(value);
    return knownAvSlugs.has(value);
  };
  return (
    <Box
      sx={{
        // Chat prose lives at body2 (14/regular) — half a step down from the
        // app default body1 (16). Comfortable reading at the chat reading-
        // column width without the spread-out feel of body1. Dense mode keeps
        // body2 size but tightens line-height for tooltips / card snippets.
        fontSize: (theme) => theme.typography.body2.fontSize,
        lineHeight: dense ? 1.4 : 1.55,
        // Long underscored identifiers (`clinical_def.get_physician_...`) have
        // no spaces and would otherwise force the chat column wider than its
        // grid cell, blowing out the layout when the SQL drawer is open.
        // `overflowWrap: anywhere` lets the renderer break inside such tokens
        // as a last resort. `minWidth: 0` lets this Box shrink below the
        // intrinsic min-content width of its children.
        minWidth: 0,
        width: '100%',
        overflowWrap: 'anywhere',
        '& p': { margin: 0, marginBottom: 0.75 },
        '& p:last-child': { marginBottom: 0 },
        '& ul, & ol': { marginTop: 0.5, marginBottom: 0.75, paddingLeft: 2.5 },
        '& li': { marginBottom: 0.25 },
        '& table': {
          borderCollapse: 'collapse',
          marginBottom: 1,
          fontSize: (theme) => theme.typography.body2.fontSize,
          // `tableLayout: fixed` + `width: 100%` makes the table respect the
          // container width regardless of content; without it, a single
          // unbreakable identifier in any cell stretches the table wider
          // than the chat column.
          tableLayout: 'fixed',
          width: '100%',
          // Quieter table style — only header underline + row underlines,
          // no full-cell grid borders. Matches the lighter feel of long-form
          // chat surfaces; full borders read as data-table chrome.
          '& th': {
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            padding: '6px 12px 6px 0',
            textAlign: 'left',
            fontWeight: 600,
            color: (theme) => theme.palette.text.primary,
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
          },
          '& td': {
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            padding: '6px 12px 6px 0',
            textAlign: 'left',
            verticalAlign: 'top',
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
          },
          '& tr:last-child td': { borderBottom: 'none' },
        },
        ...(sxProps ?? {}),
      }}
    >
      <ReactMarkdown
        remarkPlugins={[
          remarkGfm,
          remarkStripConceptGroupProposalJson,
          remarkLinkifyConceptGroupIds,
        ]}
        components={{
          a: ({ href, children: linkChildren }) => {
            // OR-6115: an in-app variable-detail link (locked library AV)
            // routes through the chat-preserving navigate handler so it lands
            // on the curated record, not the external browser. Falls back to
            // an external link when no handler is mounted or the href isn't an
            // in-app variable route.
            const variableId = parseProjectVariableHref(href);
            if (variableId && avSlugClick) {
              return (
                <Link
                  component="button"
                  type="button"
                  onClick={() => avSlugClick(variableId)}
                  data-testid={`epi-markdown-av-link-${variableId}`}
                  sx={{ verticalAlign: 'baseline', cursor: 'pointer' }}
                >
                  {linkChildren}
                </Link>
              );
            }
            // OR-6266: a project-less `[<label>](/variable/av.<slug>)` (or
            // `/variable/<uuid>`) ref routes through the same handler — the
            // surface supplies the project id. The label stays the clickable
            // name, mirroring the concept-group name-link path.
            const avRefId = parseAvRefHref(href);
            if (avRefId && avSlugClick) {
              return (
                <Link
                  component="button"
                  type="button"
                  onClick={() => avSlugClick(avRefId)}
                  data-testid={`epi-markdown-av-link-${avRefId}`}
                  sx={{ verticalAlign: 'baseline', cursor: 'pointer' }}
                >
                  {linkChildren}
                </Link>
              );
            }
            // OR-6129: a `[name](/concept-group/<id>)` link renders the name as
            // the clickable preview trigger, routing through the same handlers
            // as a bare-id chip so drill-in is identical.
            const conceptGroupId = parseConceptGroupHref(href);
            if (conceptGroupId) {
              return (
                <Link
                  component="button"
                  type="button"
                  onClick={() =>
                    navigateToConceptGroup
                      ? navigateToConceptGroup(conceptGroupId)
                      : setOpenConceptGroupId(conceptGroupId)
                  }
                  data-testid={`epi-markdown-concept-group-link-${conceptGroupId}`}
                  aria-label={`Preview concept group ${conceptGroupId}`}
                  sx={{ verticalAlign: 'baseline', cursor: 'pointer' }}
                >
                  {linkChildren}
                </Link>
              );
            }
            return (
              <Link href={href} target="_blank" rel="noreferrer noopener">
                {linkChildren}
              </Link>
            );
          },
          h1: ({ children: c }) => <Typography variant="h6">{c}</Typography>,
          h2: ({ children: c }) => (
            <Typography variant="subtitle1" fontWeight={600}>
              {c}
            </Typography>
          ),
          h3: ({ children: c }) => (
            <Typography variant="subtitle2" fontWeight={600}>
              {c}
            </Typography>
          ),
          code: ({
            className,
            children: codeChildren,
          }: {
            className?: string;
            children?: React.ReactNode;
          }) => {
            const match = /language-(\w+)/.exec(className ?? '');
            const value = String(codeChildren).replace(/\n$/, '');
            if (match) {
              return (
                <SyntaxHighlighter
                  language={match[1]}
                  PreTag="div"
                  customStyle={{ fontSize: '0.875rem' }}
                >
                  {value}
                </SyntaxHighlighter>
              );
            }
            const isConceptGroup = CONCEPT_GROUP_ID_PATTERN.test(value);
            // OR-6306: an AV-slug reference is only rendered as a clickable
            // link when a host has registered a real navigate handler.
            // Surfaces that haven't wired one up render the reference as
            // inert code (same as the default branch below) rather than
            // falling back to the legacy knowledge-browser dialog, which
            // must never surface to users.
            const linkClickHandler = isConceptGroup
              ? () =>
                  navigateToConceptGroup
                    ? navigateToConceptGroup(value)
                    : setOpenConceptGroupId(value)
              : isLinkableAvSlug(value) && avSlugClick
              ? () => avSlugClick(value)
              : null;
            if (linkClickHandler) {
              return (
                <Box
                  component="button"
                  type="button"
                  onClick={linkClickHandler}
                  data-testid={
                    isConceptGroup
                      ? `epi-markdown-concept-group-${value}`
                      : `epi-markdown-av-slug-${value}`
                  }
                  aria-label={
                    isConceptGroup
                      ? `Preview concept group ${value}`
                      : `Preview analytical variable ${value}`
                  }
                  sx={{
                    border: 0,
                    background: (theme) => theme.palette.action.hover,
                    cursor: 'pointer',
                    fontFamily: 'Menlo, Consolas, monospace',
                    fontSize: (theme) => theme.typography.body2.fontSize,
                    padding: '1px 4px',
                    borderRadius: 0.5,
                    color: (theme) => theme.palette.primary.main,
                    textDecoration: 'underline',
                    textUnderlineOffset: 2,
                    '&:hover': {
                      color: (theme) => theme.palette.primary.dark,
                      backgroundColor: (theme) => theme.palette.action.selected,
                    },
                    '&:focus-visible': {
                      outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                      outlineOffset: 1,
                    },
                  }}
                >
                  {codeChildren}
                </Box>
              );
            }
            return (
              <Box
                component="code"
                sx={{
                  fontFamily: 'Menlo, Consolas, monospace',
                  fontSize: (theme) => theme.typography.body2.fontSize,
                  backgroundColor: (theme) => theme.palette.action.hover,
                  padding: '1px 4px',
                  borderRadius: 0.5,
                }}
                className={className}
              >
                {codeChildren}
              </Box>
            );
          },
        }}
      >
        {normalized}
      </ReactMarkdown>
      {openConceptGroupId && (
        <ConceptGroupDetailsDialog
          conceptGroupId={openConceptGroupId}
          datasetReleaseId={activeDataset?.id ?? null}
          datasetReleaseDisplayName={activeDataset?.displayName ?? null}
          onClose={() => setOpenConceptGroupId(null)}
        />
      )}
    </Box>
  );
};
